import { isDrawableByPdfMake } from "../pdf-image.js";
import type { TDocumentDefinitions, Content, TableCell } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import type { AvrDocumentModel } from "../../../domain/engineering/avr-report-domain.js";
import type { AvrCriticality } from "../../../domain/engineering/avr-domain.js";
import {
  HEADER_PAGE_MARGIN_TOP,
  INSPECTION_REPORT_HEADER_STYLES,
  buildInspectionReportHeader
} from "./inspection-report-header.js";
import { fitBox } from "./image-size.js";

/** A4 paisagem (841.89pt) menos as margens laterais de 40pt. */
const USABLE_WIDTH = 762;
const CROQUI_MAX: [number, number] = [600, 240];

const BLUE = "#002B49";
const BAND_FILL = "#DCE8F7";
const BORDER = "#C9D3E0";
const LABEL = "#5A6572";
const TEXT = "#1F2933";
const MUTED = "#6B7683";

/** Cores do mapa de criticidade do modelo oficial. */
const CRITICALITY_COLORS: Record<AvrCriticality, { fill: string; color: string }> = {
  LOW: { fill: "#4caf50", color: "#FFFFFF" },
  MODERATE: { fill: "#ffc107", color: "#333333" },
  HIGH: { fill: "#ff9800", color: "#FFFFFF" },
  CRITICAL: { fill: "#f44336", color: "#FFFFFF" },
  UNCLASSIFIED: { fill: "#e0e0e0", color: "#333333" }
};

const GRID_LAYOUT = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => BORDER,
  vLineColor: () => BORDER,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  paddingLeft: () => 4,
  paddingRight: () => 4
};

const BOX_LAYOUT = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => BORDER,
  vLineColor: () => BORDER,
  paddingTop: () => 6,
  paddingBottom: () => 6,
  paddingLeft: () => 8,
  paddingRight: () => 8
};

/**
 * AVR — AVALIAÇÃO DE VIDA REMANESCENTE.
 *
 * Segue o modelo oficial (modelo_avr_mapa_criticidade 01.pdf): paisagem,
 * quadro de identificação, 1 Resumo Executivo, 2 Critério de Classificação,
 * 3 Mapa de Criticidade, 4 Memória de Cálculo, 5 Relatórios / Documentos de
 * Referência, 6 Fórmulas (as do motor API 570), 7 Análise Técnica,
 * 8 Conclusão / Decisão Técnica, 9 Signatários — com a identidade INSPECT IA
 * e o cabeçalho compartilhado dos relatórios. Paginação dinâmica: nenhuma
 * quebra forçada; um título de seção nunca fica órfão no fim da página.
 * Consome o AvrDocumentModel — o mesmo objeto do Preview.
 */
export class AvrBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const model = (snapshot.data || {}) as AvrDocumentModel;

    return {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [40, HEADER_PAGE_MARGIN_TOP + 6, 40, 44],
      header: () => this.buildHeader(model),
      pageBreakBefore: (currentNode: any, nodeQueries: any) =>
        currentNode.headlineLevel === 1 &&
        nodeQueries.getFollowingNodesOnPage().filter((n: any) => n.style !== "band").length === 0,
      footer: (currentPage: number, pageCount: number) => ({
        margin: [40, 6, 40, 0],
        stack: [
          { canvas: [{ type: "line", x1: 0, y1: 0, x2: USABLE_WIDTH, y2: 0, lineWidth: 0.5, lineColor: BORDER }] },
          {
            margin: [0, 5, 0, 0],
            columns: [
              { text: "INSPECT IA — Sistema de Inspeção e Integridade Mecânica", style: "footerText" },
              { text: `${model.avrNumber || "—"}  ·  Rev. ${model.revision || "00"}`, style: "footerText", alignment: "center" },
              { text: `Página ${currentPage} de ${pageCount}`, style: "footerText", alignment: "right" }
            ]
          }
        ]
      }),
      content: [
        this.buildIdentification(model),
        this.sectionBand("1", "RESUMO EXECUTIVO"),
        this.buildExecutiveSummary(model),
        this.sectionBand("2", "CRITÉRIO DE CLASSIFICAÇÃO"),
        this.buildClassificationCriteria(),
        this.sectionBand("3", "MAPA DE CRITICIDADE DA AVR"),
        this.buildCriticalityMap(model),
        this.sectionBand("4", "MEMÓRIA DE CÁLCULO POR COMPONENTE"),
        this.buildCalculationTable(model),
        this.sectionBand("5", "RELATÓRIOS / DOCUMENTOS DE REFERÊNCIA"),
        this.buildReferences(model),
        this.sectionBand("6", "FÓRMULAS UTILIZADAS"),
        this.buildFormulas(),
        this.sectionBand("7", "ANÁLISE TÉCNICA"),
        this.buildAnalysis(model),
        this.sectionBand("8", "CONCLUSÃO / DECISÃO TÉCNICA"),
        this.buildConclusion(model),
        ...this.buildCroquiSection(model),
        this.sectionBand(model.croqui?.image ? "10" : "9", "SIGNATÁRIOS"),
        this.buildSignatories(model)
      ],
      styles: {
        ...INSPECTION_REPORT_HEADER_STYLES,
        headerLogo: { ...INSPECTION_REPORT_HEADER_STYLES.headerLogo, fontSize: 15 },
        headerSub: { ...INSPECTION_REPORT_HEADER_STYLES.headerSub, fontSize: 8 },
        headerDocTitle: { ...INSPECTION_REPORT_HEADER_STYLES.headerDocTitle, fontSize: 17, lineHeight: 1.1, margin: [0, 2, 0, 0] },
        headerField: { ...INSPECTION_REPORT_HEADER_STYLES.headerField, fontSize: 9 },
        footerText: { fontSize: 6.8, color: MUTED },
        band: { fontSize: 9, bold: true, color: BLUE },
        label: { fontSize: 7.5, bold: true, color: LABEL },
        value: { fontSize: 8, color: TEXT },
        th: { fontSize: 7.2, bold: true, color: "#FFFFFF", fillColor: BLUE, alignment: "center" },
        td: { fontSize: 7.5, color: TEXT, alignment: "center" },
        tdLeft: { fontSize: 7.5, color: TEXT },
        boxText: { fontSize: 8, color: TEXT, lineHeight: 1.25 },
        note: { fontSize: 7.2, color: MUTED, italics: true },
        empty: { fontSize: 7.5, color: MUTED, italics: true },
        sigRole: { fontSize: 7, bold: true, color: TEXT },
        sigName: { fontSize: 8, bold: true, color: TEXT },
        sigMeta: { fontSize: 7.2, color: MUTED }
      },
      defaultStyle: { fontSize: 8, color: TEXT }
    };
  }

  // ---------------------------------------------------------------- HEADER

  /** O cabeçalho compartilhado dos relatórios (ME/RI/LP), em paisagem. */
  private buildHeader(model: AvrDocumentModel): Content {
    const fields = [
      { label: "Rel. cliente nº", value: model.clientReportNumber || "-" },
      { label: "Rel. INSPECT IA nº", value: model.avrNumber || "-" },
      { label: "Revisão", value: model.revision || "00" },
      { label: "Data de emissão", value: model.issueDate || "-" },
      { label: "Ordem de Serviço", value: model.header?.osNumber || "-" }
    ];
    if (model.header?.subOsNumber) fields.push({ label: "Sub-OS", value: model.header.subOsNumber });
    // Situação do workflow no documento (item 32): "Aguardando Análise do PLH", "Em Análise do PLH", "Assinado"…
    if (model.statusLabel) fields.push({ label: "Situação", value: model.statusLabel });

    return buildInspectionReportHeader({
      title: "AVR — AVALIAÇÃO DE\nVIDA REMANESCENTE",
      subtitle: "Sistema de Inspeção e Integridade Mecânica",
      qrReference: model.qrPayload || `INSPECT-IA|AVR|${model.avrNumber || ""}`,
      qrSize: 72,
      fieldsWidth: 200,
      usableWidth: USABLE_WIDTH,
      fields,
      unitArea: model.header?.unitArea || "-",
      tag: model.header?.equipmentTag || "-",
      visual: model.visual ?? null
    });
  }

  private sectionBand(number: string, title: string): Content {
    return {
      table: {
        widths: ["*"],
        body: [[{ text: `${number}. ${title}`, style: "band", fillColor: BAND_FILL, margin: [4, 2, 0, 2], border: [true, false, false, false] }]]
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: (i: number) => (i === 0 ? 2 : 0),
        vLineColor: () => BLUE,
        paddingTop: () => 2,
        paddingBottom: () => 2,
        paddingLeft: () => 4,
        paddingRight: () => 4
      },
      margin: [0, 10, 0, 4],
      headlineLevel: 1
    } as Content;
  }

  // ---------------------------------------------------------- IDENTIFICAÇÃO

  /**
   * Quadro do modelo: CLIENTE / Nº AVR / REVISÃO … MATERIAL / FLUIDO. O campo
   * documental "UNIDADE: mm / mm.ano" não existe: cada resultado traz a sua
   * unidade na própria coluna (mm, mm/ano, anos). A última linha fecha em
   * FLUIDO, que ocupa as colunas restantes — sem célula vazia.
   */
  private buildIdentification(model: AvrDocumentModel): Content {
    const h = model.header || ({} as AvrDocumentModel["header"]);
    const row = (a: string, av: string, b: string, bv: string, c: string, cv: string): TableCell[] => [
      { text: a, style: "label" }, { text: this.value(av), style: "value" },
      { text: b, style: "label" }, { text: this.value(bv), style: "value" },
      { text: c, style: "label" }, { text: this.value(cv), style: "value" }
    ];
    return {
      table: {
        widths: [90, "*", 70, "*", 60, "*"],
        body: [
          row("CLIENTE", h.client, "Nº AVR", model.avrNumber, "REVISÃO", model.revision || "00"),
          row("EQUIPAMENTO / TAG", h.equipmentTag, "TIPO", h.equipmentType, "DATA", model.evaluationDate),
          row("ÁREA", h.unitArea, "OS", h.osNumber, "SUB-OS", h.subOsNumber),
          [
            { text: "MATERIAL", style: "label" }, { text: this.value(h.material), style: "value" },
            { text: "FLUIDO", style: "label" }, { text: this.value(h.fluid), style: "value", colSpan: 3 }, {}, {}
          ]
        ]
      },
      layout: GRID_LAYOUT
    };
  }

  // -------------------------------------------------------------- 1 RESUMO

  private buildExecutiveSummary(model: AvrDocumentModel): Content {
    const s = model.summary || { total: 0, low: 0, moderate: 0, high: 0, critical: 0, noData: 0 };
    const cell = (label: string, value: number, crit?: AvrCriticality): TableCell[] => [
      { text: label, style: "label" },
      { text: String(value ?? 0), style: "td", bold: true, fillColor: crit ? this.lightFill(crit) : undefined }
    ];
    return {
      table: {
        widths: ["auto", 34, "auto", 34, "auto", 34, "auto", 34, "auto", 34, "auto", 34],
        body: [[
          ...cell("TOTAL AVALIADO", s.total),
          ...cell("BAIXA", s.low, "LOW"),
          ...cell("MODERADA", s.moderate, "MODERATE"),
          ...cell("ALTA", s.high, "HIGH"),
          ...cell("CRÍTICA", s.critical, "CRITICAL"),
          ...cell("SEM DADOS", s.noData, "UNCLASSIFIED")
        ]]
      },
      layout: GRID_LAYOUT
    };
  }

  // ------------------------------------------------------------ 2 CRITÉRIO

  private buildClassificationCriteria(): Content {
    const row = (range: string, label: string, crit: AvrCriticality, rule: string): TableCell[] => [
      { text: range, style: "td" },
      { text: label, style: "td", bold: true },
      { text: "", fillColor: CRITICALITY_COLORS[crit].fill },
      { text: rule, style: "tdLeft" }
    ];
    return {
      table: {
        headerRows: 1,
        widths: [150, 110, 40, "*"],
        body: [
          [{ text: "FAIXA", style: "th" }, { text: "CLASSIFICAÇÃO", style: "th" }, { text: "COR", style: "th" }, { text: "REGRA", style: "th", alignment: "left" }],
          row("> 5 anos", "BAIXA", "LOW", "Vida remanescente superior a 5 anos"),
          row("3 a 5 anos", "MODERADA", "MODERATE", "Vida remanescente entre 3 e 5 anos"),
          row("1 a < 3 anos", "ALTA", "HIGH", "Vida remanescente entre 1 e menos de 3 anos"),
          row("< 1 ano ou espessura <= mínima", "CRÍTICA", "CRITICAL", "Condição crítica / necessidade de verificação de engenharia"),
          row("Sem dados", "NÃO CLASSIFICADO", "UNCLASSIFIED", "Dados insuficientes para cálculo")
        ]
      },
      layout: GRID_LAYOUT
    };
  }

  // ---------------------------------------------------------------- 3 MAPA

  private buildCriticalityMap(model: AvrDocumentModel): Content {
    const components = Array.isArray(model.components) ? model.components : [];
    if (components.length === 0) return this.emptyBox("Nenhum componente calculado — selecione os Relatórios ME / B-Scan de origem e execute CALCULAR.");

    const columns: Array<{ title: string; crit: AvrCriticality }> = [
      { title: "< 1 ano", crit: "CRITICAL" },
      { title: "1 a 3 anos", crit: "HIGH" },
      { title: "3 a 5 anos", crit: "MODERATE" },
      { title: "> 5 anos", crit: "LOW" },
      { title: "SEM DADOS / NÃO APLICÁVEL", crit: "UNCLASSIFIED" }
    ];
    const body: TableCell[][] = [[
      { text: "COMPONENTE", style: "th", alignment: "left" },
      { text: "LOCALIZAÇÃO", style: "th", alignment: "left" },
      ...columns.map(c => ({ text: c.title, style: "th" }))
    ]];
    for (const c of components) {
      body.push([
        { text: this.value(c.component), style: "tdLeft" },
        { text: this.value(c.location), style: "tdLeft" },
        ...columns.map(col => c.criticality === col.crit
          ? { text: c.criticalityLabel, style: "td", bold: true, fillColor: CRITICALITY_COLORS[col.crit].fill, color: CRITICALITY_COLORS[col.crit].color }
          : { text: "", style: "td" })
      ]);
    }
    return {
      stack: [
        { text: "O mapa apresenta a criticidade de cada componente a partir da vida remanescente calculada pelo motor API 570 e da condição de espessura. A classificação visual facilita a priorização técnica, sem substituir a avaliação do responsável de engenharia.", style: "note", margin: [0, 0, 0, 4] },
        { table: { headerRows: 1, widths: [170, 130, "*", "*", "*", "*", "*"], body }, layout: GRID_LAYOUT },
        this.buildLegend()
      ]
    };
  }

  private buildLegend(): Content {
    const items: Array<[AvrCriticality, string]> = [["CRITICAL", "CRÍTICA"], ["HIGH", "ALTA"], ["MODERATE", "MODERADA"], ["LOW", "BAIXA"], ["UNCLASSIFIED", "SEM DADOS"]];
    return {
      margin: [0, 4, 0, 0],
      columns: items.map(([crit, label]) => ({
        width: "auto",
        margin: [0, 0, 12, 0],
        columns: [
          { width: 10, canvas: [{ type: "rect", x: 0, y: 1, w: 8, h: 8, color: CRITICALITY_COLORS[crit].fill }] },
          { width: "auto", text: label, style: "note", italics: false, margin: [3, 0, 0, 0] }
        ]
      }))
    };
  }

  // ------------------------------------------------------------- 4 MEMÓRIA

  private buildCalculationTable(model: AvrDocumentModel): Content {
    const components = Array.isArray(model.components) ? model.components : [];
    if (components.length === 0) return this.emptyBox("Sem memória de cálculo — execute CALCULAR.");
    const th = (t: string) => ({ text: t, style: "th" });
    // HISTÓRICO DE ESPESSURAS por ponto: LONGO PRAZO → CURTO PRAZO → ATUAL (espessura + data
    // na mesma célula), depois os resultados do motor API 570. Cada coluna declara a sua unidade.
    const body: TableCell[][] = [[
      { text: "COMPONENTE", style: "th", alignment: "left" },
      th("LONGO PRAZO\nE. (mm) · data"), th("CURTO PRAZO\nE. (mm) · data"), th("ATUAL\nE. (mm) · data · rel."),
      th("E. MÍNIMA (mm)"), th("PERÍODO CP (anos)"),
      th("TC LONGO PRAZO (mm/ano)"), th("TC CURTO PRAZO (mm/ano)"), th("TC ADOTADA (mm/ano)"),
      th("VR (anos)"), th("CRITICIDADE")
    ]];
    const pair = (thickness: string, date: string, extra = "") => (thickness === "—" ? "—" : [thickness, date, extra].filter(Boolean).join("\n"));
    for (const c of components) {
      const colors = CRITICALITY_COLORS[c.criticality] || CRITICALITY_COLORS.UNCLASSIFIED;
      body.push([
        { text: [c.component, c.location].filter(Boolean).join("\n"), style: "tdLeft" },
        { text: pair(c.longTermThickness, c.longTermDate), style: "td" },
        { text: pair(c.shortTermThickness, c.shortTermDate), style: "td" },
        { text: pair(c.currentThickness, c.currentDate, c.currentReport), style: "td" },
        { text: c.minimumThickness, style: "td" }, { text: c.periodYears, style: "td" },
        { text: c.longTermRate, style: "td" }, { text: c.shortTermRate, style: "td" }, { text: c.adoptedRate, style: "td", bold: true },
        { text: c.remainingLife, style: "td", bold: true },
        { text: c.criticalityLabel, style: "td", bold: true, fillColor: colors.fill, color: colors.color }
      ]);
    }
    const situations = components.filter(c => c.situationLabel && c.situationLabel !== "Calculado");
    // Rastreabilidade do histórico por ponto: relatório histórico correlacionado ou dado complementar informado no AVR.
    const origins = components
      .filter(c => c.shortTermOrigin || c.longTermOrigin)
      .map(c => ({ text: `${c.component}: ${[c.shortTermOrigin ? `curto prazo — ${c.shortTermOrigin}` : "", c.longTermOrigin ? `longo prazo — ${c.longTermOrigin}` : ""].filter(Boolean).join(" · ")}`, style: "note" }));
    return {
      stack: [
        { table: { headerRows: 1, widths: [118, 62, 62, 84, 44, 44, 52, 52, 52, 44, 70], body }, layout: GRID_LAYOUT },
        ...(situations.length > 0
          ? [{ margin: [0, 4, 0, 0] as [number, number, number, number], stack: situations.map(c => ({ text: `${c.component}: ${c.situationLabel}`, style: "note" })) }]
          : []),
        ...(origins.length > 0 ? [{ margin: [0, 4, 0, 0] as [number, number, number, number], stack: origins }] : []),
        { text: `Taxa de corrosão: método ${model.corrosionRate?.method || "API 570"} · base adotada: ${this.value(model.corrosionRate?.basisLabel)}${model.corrosionRate?.justification ? ` · justificativa: ${model.corrosionRate.justification}` : ""} · período: ${this.value(model.corrosionRate?.period)}.`, style: "note", margin: [0, 4, 0, 0] },
        ...this.buildCriticalPoint(model)
      ]
    };
  }

  private buildCriticalPoint(model: AvrDocumentModel): Content[] {
    const p = model.criticalPoint;
    if (!p) return [];
    return [{
      margin: [0, 6, 0, 0],
      table: {
        widths: [110, "*", 80, 90, 70],
        body: [[
          { text: "PONTO CRÍTICO", style: "label" },
          { text: [p.component, p.location].filter(Boolean).join(" · "), style: "value" },
          { text: `E. atual\n${p.currentThickness} mm`, style: "td" },
          { text: `TC adotada\n${p.adoptedRate} mm/ano`, style: "td" },
          { text: `VR\n${p.remainingLife} anos`, style: "td", bold: true }
        ]]
      },
      layout: GRID_LAYOUT
    }];
  }

  // ---------------------------------------------------------- 5 REFERÊNCIAS

  private buildReferences(model: AvrDocumentModel): Content {
    const refs = Array.isArray(model.references) ? model.references : [];
    if (refs.length === 0) return this.emptyBox("Nenhum relatório de origem selecionado.");
    const body: TableCell[][] = [[
      { text: "TIPO", style: "th" }, { text: "RELATÓRIO Nº", style: "th" }, { text: "REVISÃO", style: "th" }, { text: "DATA DO ENSAIO", style: "th" }, { text: "SITUAÇÃO DA FONTE", style: "th" }
    ]];
    for (const r of refs) {
      body.push([
        { text: r.kind, style: "td" },
        { text: r.reportNumber, style: "td", bold: true },
        { text: r.revision, style: "td" },
        { text: r.testDate, style: "td" },
        r.approved ? { text: "APROVADA", style: "td" } : { text: "FONTE NÃO APROVADA", style: "td", bold: true, color: "#9B1C1C" }
      ]);
    }
    const comp = model.complementary;
    const extra: Content[] = [];
    if (comp) {
      const rows: TableCell[][] = [];
      if (comp.criterionNorm) rows.push([{ text: "NORMA / CRITÉRIO", style: "label" }, { text: comp.criterionNorm, style: "value" }]);
      if (comp.minimumThicknessAdoptedMm !== null && comp.minimumThicknessAdoptedMm !== undefined) rows.push([{ text: "ESPESSURA MÍNIMA ADOTADA", style: "label" }, { text: `${String(comp.minimumThicknessAdoptedMm).replace(".", ",")} mm — ${this.value(comp.minimumThicknessOrigin)}`, style: "value" }]);
      if (comp.assumptions) rows.push([{ text: "PREMISSAS", style: "label" }, { text: comp.assumptions, style: "value" }]);
      if (comp.proposedInspectionInterval) rows.push([{ text: "INTERVALO DE INSPEÇÃO PROPOSTO", style: "label" }, { text: comp.proposedInspectionInterval, style: "value" }]);
      if (comp.observations) rows.push([{ text: "OBSERVAÇÕES", style: "label" }, { text: comp.observations, style: "value" }]);
      if (rows.length > 0) extra.push({ margin: [0, 6, 0, 0], table: { widths: [170, "*"], body: rows }, layout: GRID_LAYOUT });
    }
    return { stack: [{ table: { headerRows: 1, widths: [60, "*", 60, 90, 150], body }, layout: GRID_LAYOUT }, ...extra] };
  }

  // ------------------------------------------------------------- 6 FÓRMULAS

  /** As fórmulas que o motor API 570 aplica — documentadas, não recalculadas aqui. */
  private buildFormulas(): Content {
    const row = (name: string, formula: string, unit: string): TableCell[] => [
      { text: name, style: "label" }, { text: formula, style: "value" }, { text: unit, style: "td" }
    ];
    return {
      table: {
        widths: [190, "*", 60],
        body: [
          row("Taxa de Corrosão — Longo Prazo (TC LP)", "TC LP = (Espessura Longo Prazo − Espessura Atual) / Período(longo prazo → atual)", "mm/ano"),
          row("Taxa de Corrosão — Curto Prazo (TC CP)", "TC CP = (Espessura Curto Prazo − Espessura Atual) / Período(curto prazo → atual)", "mm/ano"),
          row("Taxa Adotada", "Maior entre TC LP e TC CP (referência conservadora da API 570), salvo base justificada", "mm/ano"),
          row("Vida Remanescente (VR)", "VR = (Espessura Atual − Espessura Mínima) / Taxa Adotada", "anos")
        ]
      },
      layout: GRID_LAYOUT
    };
  }

  // -------------------------------------------------------------- 7 ANÁLISE

  /** Só o texto do Engenheiro — nunca um parecer padrão do sistema. */
  private buildAnalysis(model: AvrDocumentModel): Content {
    return this.buildTextBox(model.analysis);
  }

  // ------------------------------------------------------------ 8 CONCLUSÃO

  private buildConclusion(model: AvrDocumentModel): Content {
    const c = model.conclusion || { status: "", worstCondition: "", observation: "" };
    const rows: TableCell[][] = [
      [{ text: "STATUS GERAL DA AVR", style: "label" }, { text: this.value(c.status), style: "value", bold: true }],
      [{ text: "CONDIÇÃO MAIS CRÍTICA", style: "label" }, { text: this.value(c.worstCondition), style: "value" }],
      [{ text: "OBSERVAÇÃO", style: "label" }, { text: this.value(c.observation), style: "value" }]
    ];
    const recs = Array.isArray(model.recommendations) ? model.recommendations : [];
    if (recs.length > 0) {
      rows.push([{ text: "RECOMENDAÇÕES VINCULADAS", style: "label" }, { stack: recs.map(r => ({ text: `${r.zr ? `ZR ${r.zr} — ` : ""}${r.scope}`, style: "value" })) }]);
    }
    return { table: { widths: [170, "*"], body: rows }, layout: GRID_LAYOUT };
  }

  // --------------------------------------------------------------- CROQUI

  private buildCroquiSection(model: AvrDocumentModel): Content[] {
    if (!model.croqui?.image) return [];
    return [
      this.sectionBand("9", `CROQUI${model.croqui.sourceReportNumber ? ` (Relatório ME ${model.croqui.sourceReportNumber})` : ""}`),
      {
        table: { widths: ["*"], body: [[{ image: model.croqui.image, fit: fitBox(model.croqui.image, CROQUI_MAX[0], CROQUI_MAX[1]), alignment: "center" }]] },
        layout: BOX_LAYOUT
      }
    ];
  }

  // ---------------------------------------------------------- SIGNATÁRIOS

  private buildSignatories(model: AvrDocumentModel): Content {
    const list = Array.isArray(model.signatories) ? model.signatories : [];
    const roles = ["ELABORADO_POR", "PLH"] as const;
    const labels: Record<string, string> = { ELABORADO_POR: "ELABORADO POR", PLH: "ENGENHEIRO PLH" };
    const cells: TableCell[] = roles.map(role => {
      const s = list.find(x => x.role === role);
      return {
        stack: [
          { text: labels[role]!, style: "sigRole", alignment: "center" },
          this.signatureImage(s?.signatureImage),
          { text: this.value(s?.name), style: "sigName", alignment: "center" },
          { text: this.value(s?.functionTitle), style: "sigMeta", alignment: "center" },
          { text: s?.credential ? `CFT/CREA: ${s.credential}` : "—", style: "sigMeta", alignment: "center" },
          { text: `Data: ${this.value(s?.date)}`, style: "sigMeta", alignment: "center", margin: [0, 2, 0, 0] },
          // Participação do PLH na análise (item 33): registrada no documento, distinta da assinatura.
          ...(role === "PLH" && model.plhReview
            ? [{ text: `Análise aprovada em ${model.plhReview.approvedAt} · hash ${model.plhReview.approvalHash.slice(0, 16)}`, style: "sigMeta", alignment: "center" as const, margin: [0, 2, 0, 0] as [number, number, number, number] }]
            : [])
        ],
        margin: [0, 4, 0, 4]
      };
    });
    return {
      table: { widths: ["*", "*"], body: [cells], dontBreakRows: true },
      layout: {
        hLineWidth: () => 0.6,
        vLineWidth: () => 0.6,
        hLineColor: () => BORDER,
        vLineColor: () => BORDER,
        paddingTop: () => 4,
        paddingBottom: () => 4,
        paddingLeft: () => 6,
        paddingRight: () => 6
      }
    };
  }

  /** A imagem REAL da assinatura cadastrada; sem ela, um espaço para assinar. */
  private signatureImage(image: string | null | undefined): Content {
    if (isDrawableByPdfMake(image)) {
      return { image, fit: [140, 42], alignment: "center", margin: [0, 6, 0, 4] };
    }
    return {
      stack: [
        { text: " ", margin: [0, 14, 0, 0] },
        { canvas: [{ type: "line", x1: 60, y1: 0, x2: 260, y2: 0, lineWidth: 0.5, lineColor: MUTED }] },
        { text: " ", margin: [0, 2, 0, 0] }
      ]
    };
  }

  // --------------------------------------------------------------- HELPERS

  private buildTextBox(text: string | null | undefined): Content {
    const value = typeof text === "string" ? text.trim() : "";
    return { table: { widths: ["*"], body: [[value ? { text: value, style: "boxText" } : { text: "—", style: "empty" }]] }, layout: BOX_LAYOUT };
  }

  private emptyBox(message: string): Content {
    return { table: { widths: ["*"], body: [[{ text: message, style: "empty", alignment: "center" }]] }, layout: BOX_LAYOUT };
  }

  private lightFill(crit: AvrCriticality): string {
    return { LOW: "#e2f0d9", MODERATE: "#fff2cc", HIGH: "#fce4d6", CRITICAL: "#f8cbad", UNCLASSIFIED: "#ededed" }[crit];
  }

  private value(input: unknown): string {
    if (input === null || input === undefined) return "—";
    const text = String(input).trim();
    return text.length > 0 ? text : "—";
  }
}

documentEngine.registerBuilder("AVR", new AvrBuilder());
